import React, { useEffect, useRef, useState } from 'react';
import Icon from '../Common/Icon';
import { arabuluculukAPI } from '../../api/api';
import { formatDateOnly, formatDateForDateInput } from '../../utils/dateUtils';
import './ArabuluculukBolumu.css';

/**
 * Dosya detayındaki "Arabuluculuk" bölümü (kullanıcı isteği, 2026-10-07).
 *
 * Başvuru tarihi, sonuç (anlaşma / kısmen / anlaşamama), son tutanak tarihi,
 * büro / dosya no ve bağlı belgeler (son tutanak, anlaşma belgesi).
 *
 * Belgeler'e arabuluculuk evrakı (UYAP UDF'si) eklenince sunucu evrakı okuyup
 * BOŞ alanları kendisi doldurur; "Belgelerden oku" aynı okumayı elle başlatır.
 * Dolu bir alan belgeyle çelişirse üzerine yazılmaz: fark burada gösterilir,
 * kullanıcı onaylarsa değişir.
 *
 * Kayıt genel düzenleme formundan ayrı uçtan (/Arabuluculuk) yapılır: tek bir
 * alan için bütün dosyayı göndermek gerekmez, belge bağlantıları da form
 * kaydında silinmez.
 */

export const ARABULUCULUK_SONUC = {
  1: { ad: 'Anlaşma sağlandı', kisa: 'Anlaşma', ton: 'olumlu' },
  2: { ad: 'Kısmen anlaşma', kisa: 'Kısmen anlaşma', ton: 'kismen' },
  3: { ad: 'Anlaşma sağlanamadı', kisa: 'Anlaşmama', ton: 'olumsuz' },
};
/** Kullanıcının söylediği sıra: anlaşma, anlaşmama, kısmen anlaşma. */
export const SONUC_SIRASI = [1, 3, 2];

const Tik = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
    <polyline points="3.2,8.6 6.6,11.8 12.8,4.6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const al = (o, ad) => o?.[ad] ?? o?.[ad.charAt(0).toLowerCase() + ad.slice(1)];
const belgeNo = (d) => d?.Id ?? d?.id;
const belgeAdi = (d) => d?.FileName ?? d?.fileName ?? '';
const belgeTuru = (d) => d?.DocumentType ?? d?.documentType ?? 0;

/** Farkı onaylayınca kayıt ucuna gidecek alan. */
const farkDegeri = (f) => {
  if (f.Alan === 'Sonuc' || f.Alan === 'TutanakBelgeId' || f.Alan === 'AnlasmaBelgeId') {
    return { [f.Alan]: parseInt(f.Deger, 10) };
  }
  return { [f.Alan]: f.Deger };
};

const ArabuluculukBolumu = ({
  caseFile, caseFileId, canEdit, documents = [], onPreview, onYenile, sonBelgeYukleme = 0,
  // Belgeler'de adında arabuluculuk geçen evrak (dosya detayı bulur); bölüm boşsa kendiliğinden okunur
  otomatikBelgeler = [],
}) => {
  const basvuru = al(caseFile, 'ArabuluculukTarihi');
  const sonTutanak = al(caseFile, 'SonTutanakTarihi');
  const sonuc = al(caseFile, 'ArabuluculukSonucu');
  const dosyaNo = al(caseFile, 'ArabuluculukDosyaNo');
  const tutanakId = al(caseFile, 'ArabuluculukTutanakBelgeId');
  const anlasmaId = al(caseFile, 'ArabuluculukAnlasmaBelgeId');

  const belgeBul = (id) => (id ? documents.find((d) => belgeNo(d) === id) : null);
  const tutanakBelge = belgeBul(tutanakId);
  const anlasmaBelge = belgeBul(anlasmaId);
  const genelBelgeler = documents.filter((d) => belgeTuru(d) === 0);

  const bilgiVar = !!(basvuru || sonTutanak || sonuc || dosyaNo || tutanakBelge || anlasmaBelge);
  const sonucBilgi = ARABULUCULUK_SONUC[sonuc];
  const devamEdiyor = !!basvuru && !sonucBilgi && !sonTutanak;

  const [duzenle, setDuzenle] = useState(false);
  const [taslak, setTaslak] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [okunuyor, setOkunuyor] = useState(false);
  const [bildirim, setBildirim] = useState(null); // { tur: 'tamam' | 'bilgi' | 'uyari' | 'hata', metin }
  const [farklar, setFarklar] = useState([]);
  const [parla, setParla] = useState(false);
  const [hizliKayit, setHizliKayit] = useState(false);

  /* Bilgiler bu bölümün dışında değişirse (belge yüklenince sunucunun
     doldurması, genel düzenleme formu) bölüm kısa süre parlar. Kendi
     kaydımız ve başka dosyaya geçiş sayılmaz. */
  const imza = `${caseFileId}#` + [basvuru, sonTutanak, sonuc, dosyaNo, tutanakId, anlasmaId].map((x) => x ?? '').join('|');
  const oncekiImza = useRef(null);
  const kendiKaydim = useRef(false);
  useEffect(() => {
    const onceki = oncekiImza.current;
    oncekiImza.current = imza;
    if (!onceki || onceki === imza) return undefined;
    if (kendiKaydim.current) { kendiKaydim.current = false; return undefined; }
    if (onceki.split('#')[0] !== imza.split('#')[0]) return undefined;
    setParla(true);
    const belgedenMi = sonBelgeYukleme && Date.now() - sonBelgeYukleme < 120000;
    setBildirim({ tur: 'tamam', metin: belgedenMi ? 'Bilgiler eklenen belgeden dolduruldu.' : 'Arabuluculuk bilgileri güncellendi.' });
    const t = setTimeout(() => setParla(false), 2600);
    return () => clearTimeout(t);
  }, [imza, sonBelgeYukleme]);

  // Başka dosyaya geçince açık düzenleme ve mesajlar kapanır.
  useEffect(() => {
    setDuzenle(false);
    setBildirim(null);
    setFarklar([]);
  }, [caseFileId]);

  const duzenlemeyiAc = () => {
    setTaslak({
      BasvuruTarihi: formatDateForDateInput(basvuru) || '',
      SonTutanakTarihi: formatDateForDateInput(sonTutanak) || '',
      Sonuc: sonuc ?? null,
      DosyaNo: dosyaNo || '',
      TutanakBelgeId: tutanakBelge ? tutanakId : null,
      AnlasmaBelgeId: anlasmaBelge ? anlasmaId : null,
    });
    setBildirim(null);
    setDuzenle(true);
  };

  const kaydet = async () => {
    if (!taslak) return;
    const simdiki = {
      BasvuruTarihi: formatDateForDateInput(basvuru) || '',
      SonTutanakTarihi: formatDateForDateInput(sonTutanak) || '',
      Sonuc: sonuc ?? null,
      DosyaNo: dosyaNo || '',
      TutanakBelgeId: tutanakBelge ? tutanakId : null,
      AnlasmaBelgeId: anlasmaBelge ? anlasmaId : null,
    };
    const alanlar = Object.keys(simdiki).filter((k) => (taslak[k] ?? '') !== (simdiki[k] ?? ''));
    if (taslak.BasvuruTarihi && taslak.SonTutanakTarihi && taslak.SonTutanakTarihi < taslak.BasvuruTarihi) {
      setBildirim({ tur: 'uyari', metin: 'Son tutanak tarihi başvuru tarihinden önce olamaz.' });
      return;
    }
    if (alanlar.length === 0) { setDuzenle(false); return; }
    setKaydediliyor(true);
    try {
      await arabuluculukAPI.kaydet(caseFileId, alanlar, {
        BasvuruTarihi: taslak.BasvuruTarihi || null,
        SonTutanakTarihi: taslak.SonTutanakTarihi || null,
        Sonuc: taslak.Sonuc ?? null,
        DosyaNo: taslak.DosyaNo?.trim() || null,
        TutanakBelgeId: taslak.TutanakBelgeId ?? null,
        AnlasmaBelgeId: taslak.AnlasmaBelgeId ?? null,
      });
      kendiKaydim.current = true;
      setDuzenle(false);
      setFarklar([]);
      setBildirim({ tur: 'tamam', metin: 'Kaydedildi.' });
      await onYenile?.();
    } catch (e) {
      setBildirim({ tur: 'hata', metin: e?.response?.data?.message || 'Kaydedilemedi. Lütfen tekrar deneyin.' });
    } finally {
      setKaydediliyor(false);
    }
  };

  const oku = async ({ otomatik = false } = {}) => {
    setOkunuyor(true);
    setBildirim(null);
    setFarklar([]);
    try {
      const r = await arabuluculukAPI.oku(caseFileId);
      if (!r?.Okundu) {
        if (!otomatik) setBildirim({ tur: 'uyari', metin: r?.Hata || 'Belgelerden bilgi okunamadı.' });
        return;
      }
      const yazilan = r.Yazilanlar || [];
      const fark = r.Farklar || [];
      setFarklar(fark);
      let metin;
      if (yazilan.length) metin = `${otomatik ? 'Belgelerdeki arabuluculuk evrakından' : 'Belgeden'} dolduruldu: ${yazilan.join(', ')}.`;
      else if (fark.length) metin = 'Belgedeki bilgiler kayıtlı olanlardan farklı; aşağıdan kontrol edin.';
      else metin = 'Belgedeki bilgiler zaten kayıtlı.';
      if (r.SonucBelirsiz) metin += ' Sonuç belgeden net anlaşılmadı, lütfen kendiniz seçin.';
      setBildirim({ tur: yazilan.length ? 'tamam' : 'bilgi', metin });
      if (yazilan.length) {
        kendiKaydim.current = true;
        setParla(true);
        setTimeout(() => setParla(false), 2600);
        await onYenile?.();
      }
    } catch (e) {
      if (!otomatik) setBildirim({ tur: 'hata', metin: e?.response?.data?.message || 'Okuma sırasında bir hata oluştu.' });
    } finally {
      setOkunuyor(false);
    }
  };

  /* Belgeler'de arabuluculuk evrakı varken bölüm boşsa kendiliğinden okunur
     (kullanıcı isteği, 2026-10-07: "belgeler kısmında arabuluculuk belgesi varsa
     otomatik ... bilgiler doldurulsun"). Aynı evrak için bir kez denenir; yeni
     evrak eklenince yeniden. Hiçbir şey bulunamazsa sessiz kalır. */
  const otoImza = otomatikBelgeler.map(belgeNo).sort((a, b) => a - b).join(',');
  const otoDenenen = useRef(new Set());
  useEffect(() => {
    if (!canEdit || bilgiVar || !otoImza || okunuyor) return;
    const anahtar = `arbOtoOkuma:${caseFileId}:${otoImza}`;
    if (otoDenenen.current.has(anahtar)) return;
    otoDenenen.current.add(anahtar);
    try {
      if (localStorage.getItem(anahtar)) return;
      localStorage.setItem(anahtar, '1');
    } catch (e) { /* tarayıcı depolaması kapalı: yine de bir kez oku */ }
    oku({ otomatik: true });
  }, [canEdit, bilgiVar, otoImza, caseFileId]);

  /** Sonuç işaret kutusu: tek tıkla kaydeder; işaretliye tıklamak işareti kaldırır. */
  const hizliSonuc = async (s) => {
    if (!canEdit || hizliKayit) return;
    const yeni = sonuc === s ? null : s;
    setHizliKayit(true);
    try {
      await arabuluculukAPI.kaydet(caseFileId, ['Sonuc'], { Sonuc: yeni });
      kendiKaydim.current = true;
      setBildirim({ tur: 'tamam', metin: yeni ? `Sonuç işaretlendi: ${ARABULUCULUK_SONUC[yeni].kisa}.` : 'Sonuç işareti kaldırıldı.' });
      await onYenile?.();
    } catch (e) {
      setBildirim({ tur: 'hata', metin: e?.response?.data?.message || 'Sonuç kaydedilemedi.' });
    } finally {
      setHizliKayit(false);
    }
  };

  /* Sonuç işaret kutuları: anlaşma / anlaşmama / kısmen — hangisiyse işaretli,
     hızlı göz atmak için (kullanıcı isteği, 2026-10-07). */
  const sonucKutulari = (
    <div className="arb-tikler" role="radiogroup" aria-label="Arabuluculuk sonucu">
      {SONUC_SIRASI.map((s) => {
        const secili = sonuc === s;
        return (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={secili}
            aria-disabled={!canEdit}
            className={`arb-tik-secenek arb-ton-${ARABULUCULUK_SONUC[s].ton}${secili ? ' isaretli' : ''}${canEdit ? '' : ' salt-okunur'}`}
            onClick={canEdit ? () => hizliSonuc(s) : undefined}
            disabled={hizliKayit}
            title={canEdit ? (secili ? 'İşareti kaldırmak için tıklayın' : 'İşaretlemek için tıklayın') : undefined}
          >
            <span className="arb-tik-kutu" aria-hidden="true">{secili && <Tik />}</span>
            {ARABULUCULUK_SONUC[s].kisa}
          </button>
        );
      })}
    </div>
  );

  const farkiUygula = async (f) => {
    try {
      await arabuluculukAPI.kaydet(caseFileId, [f.Alan], farkDegeri(f));
      kendiKaydim.current = true;
      setFarklar((o) => o.filter((x) => x.Alan !== f.Alan));
      setBildirim({ tur: 'tamam', metin: `${f.Etiket} belgedeki ile değiştirildi.` });
      await onYenile?.();
    } catch (e) {
      setBildirim({ tur: 'hata', metin: e?.response?.data?.message || 'Değiştirilemedi.' });
    }
  };

  const belgeDugmesi = (etiket, belge) => (
    <button
      type="button"
      className="arb-belge"
      onClick={() => onPreview?.(belge)}
      title={`${belgeAdi(belge)} — önizlemek için tıklayın`}
    >
      <Icon name="document" size={16} />
      <span className="arb-belge-etiket">{etiket}</span>
      <span className="arb-belge-ad">{belgeAdi(belge)}</span>
    </button>
  );

  const belgeSecici = (etiket, alan) => (
    <label className="arb-alan">
      <span className="arb-alan-etiket">{etiket}</span>
      <select
        value={taslak[alan] ?? ''}
        onChange={(e) => setTaslak((t) => ({ ...t, [alan]: e.target.value ? parseInt(e.target.value, 10) : null }))}
      >
        <option value="">— Bağlı belge yok —</option>
        {genelBelgeler.map((d) => (
          <option key={belgeNo(d)} value={belgeNo(d)}>{belgeAdi(d)}</option>
        ))}
      </select>
    </label>
  );

  const okuDugmesi = (
    <button type="button" className="arb-dugme arb-dugme-ikincil" onClick={() => oku()} disabled={okunuyor || kaydediliyor}>
      {okunuyor ? <span className="arb-donen" aria-hidden="true" /> : <Icon name="document" size={16} />}
      <span>{okunuyor ? 'Okunuyor…' : 'Belgelerden oku'}</span>
    </button>
  );

  return (
    <div className={`detail-section detail-section-force arb-bolum${parla ? ' arb-parla' : ''}`}>
      <div className="section-header arb-baslik">
        <div className="arb-baslik-sol">
          <span
            className={`arb-var${bilgiVar ? ' isaretli' : ''}`}
            role="img"
            aria-label={bilgiVar ? 'Arabuluculuk var' : 'Arabuluculuk bilgisi yok'}
            title={bilgiVar ? 'Arabuluculuk var' : 'Arabuluculuk bilgisi yok'}
          >
            {bilgiVar && <Tik />}
          </span>
          <h2>Arabuluculuk</h2>
        </div>
        {canEdit && !duzenle && (
          <div className="arb-baslik-dugmeler">
            {okuDugmesi}
            <button type="button" className="arb-dugme arb-dugme-ikincil" onClick={duzenlemeyiAc} disabled={okunuyor}>
              <Icon name="edit" size={16} />
              <span>{bilgiVar ? 'Düzenle' : 'Elle gir'}</span>
            </button>
          </div>
        )}
      </div>

      {okunuyor && (
        <div className="arb-not arb-not-bilgi" role="status">
          Belgeler okunuyor. UDF anında okunur; taranmış PDF ya da fotoğrafta biraz sürebilir.
        </div>
      )}

      {bildirim && !okunuyor && (
        <div className={`arb-not arb-not-${bildirim.tur}`} role={bildirim.tur === 'hata' ? 'alert' : 'status'}>
          <span>{bildirim.metin}</span>
          <button type="button" className="arb-not-kapat" onClick={() => setBildirim(null)} aria-label="Mesajı kapat">×</button>
        </div>
      )}

      {farklar.length > 0 && !duzenle && (
        <div className="arb-farklar">
          <div className="arb-farklar-baslik">Belgede farklı yazıyor — kayıtlı bilgiye dokunulmadı:</div>
          {farklar.map((f) => (
            <div className="arb-fark" key={f.Alan}>
              <div className="arb-fark-metin">
                <strong>{f.Etiket}</strong>
                <span>Kayıtlı: {f.Mevcut || '—'}</span>
                <span>Belgede: {f.Okunan}</span>
              </div>
              {canEdit && (
                <button type="button" className="arb-dugme arb-dugme-kucuk" onClick={() => farkiUygula(f)}>
                  Belgedekini kullan
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {duzenle && taslak ? (
        <div className="arb-duzen">
          <div className="arb-duzen-izgara">
            <label className="arb-alan">
              <span className="arb-alan-etiket">Başvuru tarihi</span>
              <input
                type="date"
                value={taslak.BasvuruTarihi}
                onChange={(e) => setTaslak((t) => ({ ...t, BasvuruTarihi: e.target.value }))}
              />
            </label>
            <label className="arb-alan">
              <span className="arb-alan-etiket">Son tutanak tarihi</span>
              <input
                type="date"
                value={taslak.SonTutanakTarihi}
                onChange={(e) => setTaslak((t) => ({ ...t, SonTutanakTarihi: e.target.value }))}
              />
            </label>
            <div className="arb-alan arb-alan-genis">
              <span className="arb-alan-etiket">Sonuç</span>
              <div className="arb-secim" role="radiogroup" aria-label="Arabuluculuk sonucu">
                {SONUC_SIRASI.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={taslak.Sonuc === s}
                    className={`arb-secim-dugme arb-ton-${ARABULUCULUK_SONUC[s].ton}${taslak.Sonuc === s ? ' secili' : ''}`}
                    onClick={() => setTaslak((t) => ({ ...t, Sonuc: t.Sonuc === s ? null : s }))}
                  >
                    <span className="arb-secim-isaret" aria-hidden="true" />
                    {ARABULUCULUK_SONUC[s].kisa}
                  </button>
                ))}
              </div>
            </div>
            <label className="arb-alan arb-alan-genis">
              <span className="arb-alan-etiket">Büro / dosya no</span>
              <input
                type="text"
                maxLength={300}
                placeholder="Ör. Ankara Arabuluculuk Bürosu 2026/1234"
                value={taslak.DosyaNo}
                onChange={(e) => setTaslak((t) => ({ ...t, DosyaNo: e.target.value }))}
              />
            </label>
            {genelBelgeler.length > 0 && belgeSecici('Son tutanak belgesi', 'TutanakBelgeId')}
            {genelBelgeler.length > 0 && belgeSecici('Anlaşma belgesi', 'AnlasmaBelgeId')}
          </div>
          <div className="arb-duzen-dugmeler">
            <button type="button" className="arb-dugme arb-dugme-ikincil" onClick={() => setDuzenle(false)} disabled={kaydediliyor}>
              Vazgeç
            </button>
            <button type="button" className="arb-dugme arb-dugme-birincil" onClick={kaydet} disabled={kaydediliyor}>
              {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      ) : bilgiVar ? (
        <div className="arb-ozet">
          {/* Sonuç en üstte ve tam genişlikte: en önemli bilgi; dar panelde
              rozet ve etiketler iki satıra kaymasın. */}
          <div className="arb-kartlar">
            <div className="arb-kart arb-kart-sonuc">
              <span className="arb-kart-etiket">
                Sonuç
                {!sonucBilgi && devamEdiyor && <span className="arb-rozet arb-ton-devam">Süreç devam ediyor</span>}
              </span>
              {sonucKutulari}
            </div>
            <div className="arb-kart">
              <span className="arb-kart-etiket">Başvuru tarihi</span>
              <span className="arb-kart-deger">{basvuru ? formatDateOnly(basvuru) : '—'}</span>
            </div>
            <div className="arb-kart">
              <span className="arb-kart-etiket">Son tutanak tarihi</span>
              <span className="arb-kart-deger">{sonTutanak ? formatDateOnly(sonTutanak) : '—'}</span>
            </div>
          </div>
          {dosyaNo && (
            <div className="arb-satir">
              <span className="arb-kart-etiket">Büro / dosya no</span>
              <span className="arb-satir-deger">{dosyaNo}</span>
            </div>
          )}
          {(tutanakBelge || anlasmaBelge) && (
            <div className="arb-belgeler">
              {tutanakBelge && belgeDugmesi('Son tutanak', tutanakBelge)}
              {anlasmaBelge && belgeDugmesi('Anlaşma belgesi', anlasmaBelge)}
            </div>
          )}
        </div>
      ) : (
        <div className="arb-bos">
          <p>Arabuluculuk bilgisi yok.</p>
          <p className="arb-bos-ipucu">
            Belgeler'e son tutanağı (UYAP UDF'si) eklediğinizde başvuru tarihi, sonuç ve son tutanak tarihi kendiliğinden dolar.
            Önceden eklenmiş evrak için "Belgelerden oku"ya basın.
          </p>
          {canEdit && <div className="arb-bos-sonuc">{sonucKutulari}</div>}
        </div>
      )}
    </div>
  );
};

export default ArabuluculukBolumu;
